import React, { useMemo, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { useProject } from "../../state/ProjectContext";
import { Pill, Modal, Field } from "../ui-prim";
import { statusClass, ini, toast } from "../../lib/utils";

const COLORS = ["#22C55E", "#3B82F6", "#A855F7", "#F59E0B", "#EF4444", "#A1A1AA"];

// Matrix axis mapping (percentage from top-left of plot area):
//   X = interest (Low → High, left → right)
//   Y = influence (High → Low, top → bottom)
const INTEREST_X  = { High: 78, Medium: 50, Low: 22 };
const INFLUENCE_Y = { High: 22, Medium: 50, Low: 78 };

// Deterministically arrange stakeholders that share the same (influence, interest)
// in a small ring around the quadrant center so they don't fully overlap.
function computeMatrixPositions(stakeholders) {
  const groups = {};
  stakeholders.forEach(s => {
    const key = `${s.influence || "Medium"}|${s.interest || "Medium"}`;
    (groups[key] ||= []).push(s);
  });
  const out = {};
  Object.entries(groups).forEach(([key, group]) => {
    const [inf, intst] = key.split("|");
    const cx = INTEREST_X[intst]  ?? 50;
    const cy = INFLUENCE_Y[inf]   ?? 50;
    if (group.length === 1) {
      out[group[0].id] = { x: cx, y: cy };
      return;
    }
    // Spread on a small ring. Use a stable sort by id so position is consistent across renders.
    const sorted = [...group].sort((a, b) => (a.id > b.id ? 1 : -1));
    const n = sorted.length;
    const radius = Math.min(9, 4 + n * 0.7);
    sorted.forEach((s, i) => {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      out[s.id] = {
        x: Math.max(6, Math.min(94, cx + Math.cos(angle) * radius)),
        y: Math.max(6, Math.min(94, cy + Math.sin(angle) * radius)),
      };
    });
  });
  return out;
}

export default function Stakeholders() {
  const { doc, mutate, readOnly } = useProject();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newColor, setNewColor] = useState(COLORS[1]);

  const matrixPos = useMemo(() => computeMatrixPositions(doc.stakeholders), [doc.stakeholders]);

  const addS = () => {
    const g = (k) => document.getElementById("ns-" + k)?.value;
    const name = g("name");
    if (!name) return toast("Name required");
    mutate(d => ({ ...d, stakeholders: [...d.stakeholders, {
      id: "S" + Date.now(), name,
      title: g("title") || "",
      role: g("role") || "Stakeholder",
      dept: g("dept") || "",
      influence: g("inf"),
      interest: g("int"),
      engagement: g("eng"),
      color: newColor,
    }] }));
    setShowAdd(false);
    setNewColor(COLORS[1]);
  };

  const saveEdit = (id) => {
    const g = (k) => document.getElementById(`es-${k}-${id}`)?.value;
    mutate(d => ({ ...d, stakeholders: d.stakeholders.map(s => s.id === id ? {
      ...s,
      name: g("name") ?? s.name,
      title: g("title") ?? s.title,
      role: g("role") ?? s.role,
      dept: g("dept") ?? s.dept,
      influence: g("inf") ?? s.influence,
      interest: g("int") ?? s.interest,
      engagement: g("eng") ?? s.engagement,
    } : s) }));
    setEditing(null);
    toast("Saved");
  };

  const setColor = (id, color) => {
    mutate(d => ({ ...d, stakeholders: d.stakeholders.map(s => s.id === id ? { ...s, color } : s) }));
  };

  const remove = (id) => {
    if (!window.confirm("Remove this stakeholder?")) return;
    mutate(d => ({ ...d, stakeholders: d.stakeholders.filter(s => s.id !== id) }));
  };

  return (
    <>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">People</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Stakeholder Register</h2>
        </div>
        <div className="flex gap-2">
          <Pill tone="blue">{doc.stakeholders.length} stakeholders</Pill>
          {!readOnly && <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)} data-testid="btn-add-stakeholder"><Plus size={12}/> Add</button>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-5">
        {doc.stakeholders.map(s => {
          const isEdit = editing === s.id;
          return (
            <div key={s.id} className="card p-4 relative group">
              <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => setEditing(isEdit ? null : s.id)} data-testid={`btn-edit-stake-${s.id}`}>{isEdit ? <X size={11}/> : <Pencil size={11}/>}</button>}
                {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => remove(s.id)}><Trash2 size={11}/></button>}
              </div>
              {isEdit ? (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-sm grid place-items-center font-mono text-xs font-bold"
                         style={{ background: s.color + "22", color: s.color, border: `1px solid ${s.color}55` }}>{ini(s.name)}</div>
                    <div className="flex-1 space-y-1">
                      <input id={`es-name-${s.id}`} className="fld" defaultValue={s.name}/>
                      <input id={`es-title-${s.id}`} className="fld" defaultValue={s.title || ""} placeholder="Job title"/>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <input id={`es-role-${s.id}`} className="fld" defaultValue={s.role}/>
                    <input id={`es-dept-${s.id}`} className="fld" defaultValue={s.dept}/>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <select id={`es-inf-${s.id}`} className="fld" defaultValue={s.influence}>{["High","Medium","Low"].map(o=><option key={o}>{o}</option>)}</select>
                    <select id={`es-int-${s.id}`} className="fld" defaultValue={s.interest}>{["High","Medium","Low"].map(o=><option key={o}>{o}</option>)}</select>
                    <select id={`es-eng-${s.id}`} className="fld" defaultValue={s.engagement}>{["Champion","Lead","Supporter","Neutral","Monitor"].map(o=><option key={o}>{o}</option>)}</select>
                  </div>
                  <div className="flex gap-1 mb-2">
                    {COLORS.map(c => (
                      <button key={c} onClick={() => setColor(s.id, c)}
                              className="w-5 h-5 rounded-full"
                              style={{ background: c, outline: s.color === c ? "2px solid #FAFAFA" : "none", outlineOffset: 1 }}/>
                    ))}
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={() => saveEdit(s.id)}>Save</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 pr-12">
                  <div className="w-10 h-10 rounded-sm grid place-items-center font-mono text-xs font-bold shrink-0"
                       style={{ background: s.color + "22", color: s.color, border: `1px solid ${s.color}55` }}>{ini(s.name)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm font-bold text-zinc-900 truncate">{s.name}</div>
                    {s.title && <div className="font-mono text-[10px] text-amber-700 mt-0.5 truncate">{s.title}</div>}
                    <div className="text-xs text-zinc-700 mt-0.5">{s.role}</div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{s.dept}</div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      <Pill tone={s.influence === "High" ? "amber" : "blue"}>Inf: {s.influence}</Pill>
                      <Pill tone={s.interest === "High" ? "purple" : "neutral"}>Int: {s.interest}</Pill>
                      <Pill tone={statusClass(s.engagement).replace("pill-", "")}>{s.engagement}</Pill>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="card p-5">
        <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Influence × Interest Matrix</div>
            <div className="font-mono text-[10px] text-zinc-500 mt-0.5">Positions reflect each stakeholder's recorded influence &amp; interest.</div>
          </div>
          <div className="flex items-center gap-3 font-mono text-[10px] text-zinc-500 uppercase tracking-[0.1em]">
            <span>X · Interest</span>
            <span className="text-zinc-300">·</span>
            <span>Y · Influence</span>
          </div>
        </div>
        <div className="relative pl-12 pr-3 pb-10 pt-3">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-3 bottom-10 flex flex-col justify-between font-mono text-[10px] text-zinc-500 uppercase tracking-[0.1em] items-end pr-2 select-none">
            <span>High</span><span>Med</span><span>Low</span>
          </div>
          {/* X-axis labels (under the plot) */}
          <div className="absolute left-12 right-3 bottom-2 flex justify-between font-mono text-[10px] text-zinc-500 uppercase tracking-[0.1em] select-none">
            <span>Low</span><span>Med</span><span>High</span>
          </div>

          {/* Plot area */}
          <div className="relative h-72 border border-[var(--border)] rounded-sm bg-zinc-50 overflow-hidden" data-testid="influence-interest-matrix">
            {/* Quadrant dividers at 50% */}
            <div className="absolute inset-y-0 left-1/2 w-px bg-[var(--border-h)]" />
            <div className="absolute inset-x-0 top-1/2 h-px bg-[var(--border-h)]" />
            {/* Tertile guidelines at 33% and 66% (subtle) */}
            <div className="absolute inset-y-0 w-px bg-zinc-200" style={{ left: "33.33%" }} />
            <div className="absolute inset-y-0 w-px bg-zinc-200" style={{ left: "66.66%" }} />
            <div className="absolute inset-x-0 h-px bg-zinc-200" style={{ top: "33.33%" }} />
            <div className="absolute inset-x-0 h-px bg-zinc-200" style={{ top: "66.66%" }} />

            {/* Quadrant labels */}
            <div className="absolute top-2 left-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Keep satisfied</div>
            <div className="absolute top-2 right-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Manage closely</div>
            <div className="absolute bottom-2 left-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Monitor</div>
            <div className="absolute bottom-2 right-3 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">Keep informed</div>

            {doc.stakeholders.map(s => {
              const p = matrixPos[s.id] || { x: 50, y: 50 };
              return (
                <div key={s.id}
                     title={`${s.name} · ${s.role}\nInfluence: ${s.influence} · Interest: ${s.interest}`}
                     data-testid={`matrix-dot-${s.id}`}
                     className="absolute w-9 h-9 -translate-x-1/2 -translate-y-1/2 rounded-full grid place-items-center font-mono text-[10px] font-bold transition-all duration-300 ease-out hover:scale-125 hover:z-10 shadow-sm cursor-default"
                     style={{ left: `${p.x}%`, top: `${p.y}%`, background: s.color + "33", border: `1.5px solid ${s.color}`, color: s.color }}>
                  {ini(s.name)}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Stakeholder"
             footer={<><button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
                       <button className="btn btn-primary" onClick={addS} data-testid="btn-save-stakeholder">Add</button></>}>
        <Field label="Full Name"><input id="ns-name" className="fld" placeholder="First and last" data-testid="input-stake-name"/></Field>
        <Field label="Job title (optional)"><input id="ns-title" className="fld"/></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Project role"><input id="ns-role" className="fld" placeholder="e.g. Executive Sponsor"/></Field>
          <Field label="Department"><input id="ns-dept" className="fld"/></Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Influence">
            <select id="ns-inf" className="fld" defaultValue="Medium">{["High","Medium","Low"].map(o=><option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Interest">
            <select id="ns-int" className="fld" defaultValue="Medium">{["High","Medium","Low"].map(o=><option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Engagement">
            <select id="ns-eng" className="fld" defaultValue="Supporter">{["Champion","Lead","Supporter","Neutral","Monitor"].map(o=><option key={o}>{o}</option>)}</select>
          </Field>
        </div>
        <Field label="Color">
          <div className="flex gap-2">
            {COLORS.map(c => (
              <button key={c} onClick={() => setNewColor(c)} className="w-6 h-6 rounded-full"
                      style={{ background: c, outline: newColor === c ? "2px solid #FAFAFA" : "none", outlineOffset: 2 }}/>
            ))}
          </div>
        </Field>
      </Modal>
    </>
  );
}
